import React, { useEffect, useState } from "react";
import "./Samsara.css";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import useFetchSummary from "../../hooks/useFetchSummary";
import useWindowWidth from "../../hooks/useWindowWidth";
import Spinner from "../../components/Loaders/Spinner/Spinner";
import Button from "../../components/Buttons/Button";
import ShowMoreText from "../../components/ShowMoreText/ShowMoreText";
import HorizontalLoader from "../../components/Loaders/HorizontalLoader/HorizontalLoader";
import SearchBar from "../../components/SearchBar/SearchBar";
import SamsaraTile from "../../components/Tiles/Samsara/SamsaraTile";
import { useLocation } from "react-router-dom";
import SuggestedQuestions from "../../components/SuggestedQuestions/SuggestedQuestions";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import useDocumentTitle from "../../hooks/useDocumentTitle.tsx";
import { useDispatch } from "react-redux";
import { activateSearch, deactivateSearch } from "../../redux/actions/searchActions";

interface ResponseItem {
  title: string;
  description: string;
  link: string;
  image: string;
}

interface MarketingMessage {
  link: string;
  message: string;
}

interface Data {
  responses: ResponseItem[];
  marketing_message: MarketingMessage;
}

interface ErrorType {
  message: string | null;
  status: number | null;
}

const Samsara: React.FC = () => {
  const [text, setText] = useState("");
  const [data, setData] = useState<Data>({
    responses: [],
    marketing_message: { link: "", message: "" },
  });
  const location = useLocation();
  const [tone, setTone] = useState<string>("");
  const [generationType, setGenerationType] = useState<string>("");
  const [model, setModel] = useState<string>("");
  const [summary, setSummary] = useState<string>("");
  const [loadingSummary, setLoadingSummary] = useState<boolean>(false);
  const { fetchSummary } = useFetchSummary(setSummary, setLoadingSummary);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorType>({
    message: null,
    status: null,
  });
  const width = useWindowWidth();
  const { token } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  useDocumentTitle("Samsara");

  const fetchData = async ({ text }: { text: string }) => {
    const requestID = uuidv4();
    try {
      (window as any).dataLayer?.push({ requestId: requestID });
      dispatch(deactivateSearch());
      setLoading(true);
      const queryParams: { [key: string]: string } = {
        query: text,
        company: "samsara",
        requestId: requestID,
      };
      if (tone !== "") {
        queryParams["tone"] = tone;
      }
      if (generationType !== "") {
        queryParams["generation_type"] = generationType;
      }
      if (model !== "") {
        queryParams["model"] = model;
      }
      if (token) {
        queryParams["token"] = token;
      }
      const res = await axios.post<Data>(
        `${process.env.REACT_APP_BACKEND_URL}/post_query`,
        queryParams
      );
      setData(res.data);
      fetchSummary(queryParams);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setError({
          status: e.status || null,
          message: e.message || null,
        });
      } else {
        setError({ message: "An unexpected error occurred", status: 500 });
      }
      setLoading(false);
      dispatch(activateSearch());
      alert(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (text: string) => {
    setText(text);
    fetchData({ text });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const toneParameter = searchParams.get("tone");
    const generationTypeParameter = searchParams.get("generation_type");
    const modelParameter = searchParams.get("model");

    if (toneParameter) {
      setTone(toneParameter);
      // Do something with the parameter value
    }
    if (generationTypeParameter) {
      setGenerationType(generationTypeParameter);
      // Do something with the parameter value
    }
    if (modelParameter) {
      setModel(modelParameter);
      // Do something with the parameter value
    }
  }, [location.search]);

  return (
    <div>
      <div className="navbar samsara">
        <svg
          data-v-6a4c947d=""
          xmlns="http://www.w3.org/2000/svg"
          height="48"
          viewBox="0 0 200 48"
          width="200px"
          className="samsara-logo h-full w-full transition-all"
        >
          <g fill-rule="evenodd">
            <g>
              <path d="m25.7276319 15.4203026c-.4009916.9163637-1.0168991 1.6439648-1.8295588 2.1625338-.7174034.4577595-1.5171681.7049384-2.3769706.7350615-.3526008.0085866-.6728949-.2777243-.6853739-.6493372-.012479-.3713314.2735672-.6831202.6397563-.6957889.6227017-.0219589 1.2006176-.2000234 1.7172479-.5295483.5856806-.3737244 1.0311806-.9020059 1.3233277-1.5699238.1731807-.3939941.1442017-.8328915-.078895-1.2043636-.2197689-.3665455-.5852647-.5906393-1.0017857-.6149912-.8943277-.0522229-1.6340546.220997-2.2159916.8117771-.9409159.9552141-1.3249916 2.6597067-1.489021 3.3874487-.1411512.6237184-1.7067101 7.3739824-2.3398109 10.1020997-.0992773.429607-.4721218.7302756-.9076387.7306979h-.0004159c-.4351009 0-.8085-.3003871-.9086093-.7305572l-.2287815-.9864633c-.7185126-3.0984633-1.984021-8.5573724-2.1092269-9.1109912-.1654159-.7325279-.5494916-2.4370205-1.4908235-3.3925161-.5817983-.5904986-1.328042-.8625924-2.21543697-.8114956-.41707563.0243519-.78215546.2484457-1.00220168.6149912-.22281933.3713313-.25165967.8103695-.07944958 1.2043636.29297899.6679179.73889496 1.1963402 1.32415966 1.5699238.51663027.3295249 1.09454627.5075894 1.71738657.5295483.3660504.0126687.6522353.3244575.6396176.6957889-.012479.3713314-.3270882.6598944-.6853739.6493372-.8598025-.0301231-1.65956724-.277302-2.37683195-.7350615-.81238235-.5180059-1.42828991-1.2456071-1.83011344-2.1625338-.34691597-.7922111-.28784874-1.7087155.15848319-2.4520821.44355882-.7385806 1.21281933-1.208868 2.05834034-1.2581349 1.26897476-.0767155 2.38113026.3424751 3.22886976 1.2036599 1.2111554 1.228997 1.6552689 3.1982639 1.8448109 4.0390381.1173025.5195542 1.2239118 5.2926686 1.9551807 8.4458885.7315462-3.153783 1.8377395-7.9261935 1.9542101-8.4416656.1905126-.8449971.634626-2.814264 1.8452269-4.043261.8485714-.8614663 1.9639159-1.2793901 3.2294243-1.2036599.845521.0492669 1.6146429.5195543 2.0583404 1.2584165.4463319.7433665.5053991 1.6600117.1579285 2.4518005m-5.9273823-10.6434721c-.8886429 0-1.7933698.15357185-2.6885294.45677419-.4073698.13808798-.8514832.13780645-1.2581597.00028153-.8955756-.30348387-1.8001639-.45705572-2.6889454-.45705572-5.10210502 0-9.41262602 5.17738416-9.41262602 11.3061818 0 6.8227566 5.14092857 12.8462639 12.50214702 14.6480235.1485.0363167.3080925.0363167.4565925 0 7.3612185-1.8017596 12.5022857-7.8252669 12.5022857-14.6480235 0-6.12879764-4.3106597-11.3061818-9.4127647-11.3061818"></path>
              <path d="m17.0215109 32.0393384c-.1756764.0432141-.3568991.0650323-.5390924.0650323-.1820546 0-.363416-.0218182-.5390924-.0650323-7.95896223-1.947871-13.51724375-8.5093724-13.51724375-15.9562698 0-6.85836948 4.91783194-12.65229323 10.73858825-12.65229323 1.0318739 0 2.0773361.17707918 3.1081008.52602932.1354664.04560704.2835504.0457478.4198488-.00028152 1.0303487-.34866862 2.0756722-.5257478 3.1076848-.5257478 5.8207563 0 10.7385883 5.79392375 10.7385883 12.65229323 0 7.4468974-5.5584202 14.0083988-13.5173824 15.9562698zm12.7374454-1.8503226c0 .3718945-.297.6732669-.6629118.6732669-.3660504 0-.6630504-.3013724-.6630504-.6732669v-.9390264c0-.3718944.297-.6731261.6630504-.6731261.3659118 0 .6629118.3012317.6629118.6731261zm0 4.268481c0 .3716129-.297.6729853-.6629118.6729853-.3660504 0-.6630504-.3013724-.6630504-.6729853v-.9393079c0-.3718945.297-.6729854.6630504-.6729854.3659118 0 .6629118.3010909.6629118.6729854zm-4.2047269 2.1342404c0 .3717537-.2971386.6729854-.6630504.6729854-.3660504 0-.6629118-.3012317-.6629118-.6729854v-.9391671c0-.3718944.2968614-.6729853.6629118-.6729853.3659118 0 .6630504.3010909.6630504.6729853zm0 4.2681995c0 .3718944-.2971386.6729853-.6630504.6729853-.3660504 0-.6629118-.3010909-.6629118-.6729853v-.9391672c0-.3717537.2968614-.6729853.6629118-.6729853.3659118 0 .6630504.3012316.6630504.6729853zm-4.2044496-2.1340997c0 .3718944-.297.6729853-.6629117.6729853-.3660505 0-.6630505-.3010909-.6630505-.6729853v-.9391672c0-.3718944.297-.6729853.6630505-.6729853.3659117 0 .6629117.3010909.6629117.6729853zm0 4.2681994c0 .3718944-.297.6731261-.6629117.6731261-.3660505 0-.6630505-.3012317-.6630505-.6731261v-.9391672c0-.3717537.297-.6729853.6630505-.6729853.3659117 0 .6629117.3012316.6629117.6729853zm-4.2043109-6.4022992c0 .3717537-.297.6729854-.6630504.6729854-.3659118 0-.6629118-.3012317-.6629118-.6729854v-.9391671c0-.3718944.297-.6729853.6629118-.6729853.3660504 0 .6630504.3010909.6630504.6729853zm0 4.2681995c0 .3718944-.297.6729853-.6630504.6729853-.3659118 0-.6629118-.3010909-.6629118-.6729853v-.9391672c0-.3717537.297-.6729853.6629118-.6729853.3660504 0 .6630504.3012316.6630504.6729853zm-4.2044496-2.1340997c0 .3718944-.2968613.6729853-.6629117.6729853-.3660505 0-.6629118-.3010909-.6629118-.6729853v-.9391672c0-.3718944.2968613-.6729853.6629118-.6729853.3660504 0 .6629117.3010909.6629117.6729853zm0 4.2681994c0 .3718944-.2968613.6731261-.6629117.6731261-.3660505 0-.6629118-.3012317-.6629118-.6731261v-.9391672c0-.3717537.2968613-.6729853.6629118-.6729853.3660504 0 .6629117.3012316.6629117.6729853zm-4.20472686-6.4022992c0 .3717537-.297.6729854-.66305042.6729854-.36591177 0-.66291177-.3012317-.66291177-.6729854v-.9391671c0-.3718944.297-.6729853.66291177-.6729853.36605042 0 .66305042.3010909.66305042.6729853zm0 4.2681995c0 .3718944-.297.6729853-.66305042.6729853-.36591177 0-.66291177-.3010909-.66291177-.6729853v-.9391672c0-.3717537.297-.6729853.66291177-.6729853.36605042 0 .66305042.3012316.66305042.6729853zm-4.20444958-10.6709209c0 .3718945-.29686135.6732669-.66291177.6732669s-.66291176-.3013724-.66291176-.6732669v-.9390264c0-.3718944.29686134-.6731261.66291176-.6731261s.66291177.3012317.66291177.6731261zm0 4.268481c0 .3716129-.29686135.6729853-.66291177.6729853s-.66291176-.3013724-.66291176-.6729853v-.9393079c0-.3718945.29686134-.6729854.66291176-.6729854s.66291177.3010909.66291177.6729854zm11.95057564-34.45738419c-9.10300842 0-16.48239077 7.49151906-16.48239077 16.73285629v14.4561642c0 9.2411965 7.37938235 16.7328563 16.48239077 16.7328563 9.1030084 0 16.4823907-7.4916598 16.4823907-16.7328563v-14.4561642c0-9.24133723-7.3793823-16.73285629-16.4823907-16.73285629z"></path>
            </g>
            <g transform="translate(43 14)">
              <path d="m9.0004 9.231c-3.02-.771-4.263-1.244-4.263-2.961 0-1.243 1.125-2.191 3.079-2.191 2.013 0 3.256.829 4.204 2.073l2.428-2.014c-1.54-2.012-3.613-3.138-6.691-3.138-3.849 0-6.396 2.191-6.396 5.388 0 3.435 2.31 4.915 6.159 5.981 3.375.947 4.737 1.658 4.737 3.376 0 1.597-1.422 2.604-3.908 2.604-2.606 0-4.56-1.183-5.744-3.138l-2.605 1.835c1.776 2.725 4.618 4.382 8.349 4.382 4.618 0 7.401-2.19 7.401-5.862 0-3.553-2.605-5.27-6.75-6.335"></path>
              <path d="m29.4862 18.231c-3.849 0-6.395-2.901-6.395-6.987 0-4.145 2.546-7.046 6.395-7.046 3.79 0 6.276 2.901 6.276 7.046 0 4.086-2.486 6.987-6.276 6.987zm6.158-13.441c-1.362-2.428-3.671-3.79-6.869-3.79-5.388 0-9.474 4.382-9.474 10.244 0 5.802 4.086 10.184 9.474 10.184 3.198 0 5.507-1.361 6.869-3.789v3.316h3.612v-19.481h-3.612z"></path>
              <path d="m67.4393.9996c-3.139 0-5.329 1.185-6.869 3.612-1.184-2.427-3.256-3.612-5.862-3.612-2.665 0-4.737 1.126-5.803 3.139v-2.664h-3.612v19.48h3.612v-11.191c0-3.553 1.836-5.506 5.034-5.506 2.782 0 4.145 1.657 4.145 4.736v11.961h3.611v-11.309c0-3.435 1.777-5.388 5.034-5.388 2.723 0 4.144 1.597 4.144 4.678v12.019h3.612v-12.434c0-4.738-2.96-7.521-7.046-7.521"></path>
              <path d="m87.5092 9.231c-3.02-.771-4.264-1.244-4.264-2.961 0-1.243 1.126-2.191 3.079-2.191 2.014 0 3.257.829 4.205 2.073l2.428-2.014c-1.54-2.012-3.613-3.138-6.692-3.138-3.849 0-6.395 2.191-6.395 5.388 0 3.435 2.309 4.915 6.159 5.981 3.375.947 4.736 1.658 4.736 3.376 0 1.597-1.421 2.604-3.908 2.604-2.605 0-4.559-1.183-5.743-3.138l-2.605 1.835c1.776 2.725 4.618 4.382 8.348 4.382 4.619 0 7.402-2.19 7.402-5.862 0-3.553-2.605-5.27-6.75-6.335"></path>
              <path d="m107.9945 18.231c-3.849 0-6.395-2.901-6.395-6.987 0-4.145 2.546-7.046 6.395-7.046 3.79 0 6.276 2.901 6.276 7.046 0 4.086-2.486 6.987-6.276 6.987zm6.158-13.441c-1.362-2.428-3.671-3.79-6.868-3.79-5.389 0-9.475 4.382-9.475 10.244 0 5.802 4.086 10.184 9.475 10.184 3.197 0 5.506-1.361 6.868-3.789v3.316h3.612v-19.481h-3.612z"></path>
              <path d="m147.0219 18.231c-3.849 0-6.395-2.901-6.395-6.987 0-4.145 2.546-7.046 6.395-7.046 3.79 0 6.276 2.901 6.276 7.046 0 4.086-2.486 6.987-6.276 6.987zm6.158-16.757v3.316c-1.362-2.428-3.671-3.79-6.868-3.79-5.389 0-9.475 4.382-9.475 10.244 0 5.802 4.086 10.184 9.475 10.184 3.197 0 5.506-1.361 6.868-3.789v3.316h3.612v-19.481z"></path>
              <path d="m127.4135 5.9732v-4.499h-3.611v19.48h3.611v-10.146l7.997-7.106-2.401-2.703z"></path>
            </g>
          </g>
        </svg>
        <div className="navbar samara right-container">
          <p className="samsara-login-button">LOGIN</p>
          <Button
            text="Partner Portal"
            type="samsara"
            link="https://www.partners-samsara.com/s/login/?language=en_US&ec=302&inst=5e&startURL=%2FEnglish%2F"
          />
        </div>
      </div>
      <div className="top-container samsara">
        <div className="top-container-bg-image">
          <img
            src="https://images.ctfassets.net/bx9krvy0u3sx/5fDjuF5AOslVc64LM0PRnH/d42fdeb25cc836239ee175db7ecf4326/hero_pattern-01.svg?w=2160&q=80"
            alt=""
          />
        </div>
        <div className="top-master-container samsara">
          <SearchBar
            fetchData={fetchData}
            type={"samsara"}
            text={text}
            setText={setText}
          />
          <div className="suggested-question-container">
            <SuggestedQuestions
              handleSuggestionClick={handleSuggestionClick}
              question="How to reduce insurance premiums?"
              company="samsara"
            />
            <SuggestedQuestions
              handleSuggestionClick={handleSuggestionClick}
              question="How does Samsara help schools and governments?"
              company="samsara"
            />
            <SuggestedQuestions
              handleSuggestionClick={handleSuggestionClick}
              question="What is connected operations cloud?"
              company="samsara"
            />
            <SuggestedQuestions
              handleSuggestionClick={handleSuggestionClick}
              question="How to build a world-class safety program?"
              company="samsara"
            />
            <SuggestedQuestions
              handleSuggestionClick={handleSuggestionClick}
              question="How to optimize fleet operations with real-time data?"
              company="samsara"
            />
          </div>
          <p className="result-desc samsara">
            {loadingSummary || loading ? (
              <HorizontalLoader type="samsara" />
            ) : width < 765 ? (
              <ShowMoreText
                text={summary}
                maxLength={200}
                type={generationType}
              />
            ) : generationType == "bullet_point" ? (
              <ul className="bullet-point-summary">
                {summary.split("###").map((item) => {
                  let temp = item.trim();
                  if (temp.length > 0) {
                    return <li>{item}</li>;
                  }
                })}
              </ul>
            ) : (
              summary
            )}
          </p>
          {loading == false &&
            loadingSummary == false &&
            data.responses.length > 0 && (
              <div className="buttons-container">
                <Button
                  text="Submit deal registrations"
                  type="samsara"
                  link="https://www.partners-samsara.com/s/login/?language=en_US#loginSection"
                />
              </div>
            )}
        </div>
      </div>

      <div className="samsara-cards-container">
        {loading == true ? (
          <div className="spinner-container">
            <Spinner type="samsara" />
          </div>
        ) : (
          <div className="samsara-tiles-container">
            {data.responses.map((item, i) => (
              <SamsaraTile
                key={i}
                title={item.title}
                desc={item.description}
                imgUrl={item.image}
                link={item.link}
              />
            ))}
          </div>
        )}
      </div>
      <footer className="footer samsara">
        <div className="footer-links samsara">
          <a href="/privacy-policy">
            Privacy Policy <span>|</span>
          </a>
          <a href="/candidate-privacy-notice">
            Candidate Privacy Notice <span>|</span>
          </a>

          <a href="/website-terms-of-service">
            Website Terms of Service <span>|</span>
          </a>

          <a href="/cookie-policy">
            Cookie Policy <span>|</span>
          </a>

          <a href="/privacy-choices">
            Your Privacy Choices <span>|</span>
          </a>

          <a href="/hardware-warranty">
            Hardware Warranty <span>|</span>
          </a>

          <a href="/product-terms-of-service">
            Product Terms of Service <span>|</span>
          </a>

          <a href="/hosted-software-sla">
            Hosted Software SLA <span>|</span>
          </a>

          <a href="/partner-code-of-conduct">
            Partner Code of Conduct <span>|</span>
          </a>

          <a href="/data-protection-addendum">
            Data Protection Addendum <span>|</span>
          </a>

          <a href="/modern-slavery-statement">
            Modern Slavery Statement <span>|</span>
          </a>
        </div>
        <div className="footer-copyright">&copy; 2024 Samsara Inc.</div>
      </footer>
    </div>
  );
};

export default Samsara;
